// src/services/geocodingService.ts
import { logger } from '../utils/logger';

export interface GeocodeResult {
  lat: number;
  lng: number;
  displayName: string;
  city: string;
  country: string;
}

export const geocodingService = {
  /**
   * Geocode using Nominatim (OpenStreetMap) - FREE, no API key needed
   * Rate limit: 1 request per second
   * Best for: Development and small apps
   */
  async geocodeWithNominatim(query: string): Promise<GeocodeResult | null> {
    try {
      // Clean up the query
      const cleanQuery = query.trim();
      if (!cleanQuery) return null;

      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(cleanQuery)}&format=json&limit=1&addressdetails=1`;

      logger.log('[Nominatim] Geocoding query:', cleanQuery);
      logger.log('[Nominatim] Request URL:', url);

      const response = await fetch(url, {
        headers: {
          'User-Agent': 'Dice-Cat-App/1.0', // Nominatim requires a user agent
        },
      });

      logger.log('[Nominatim] Response status:', response.status);

      if (!response.ok) {
        logger.error('Nominatim geocoding failed:', response.status, response.statusText);
        const errorText = await response.text();
        logger.error('Nominatim error response:', errorText);
        return null;
      }

      const data = await response.json();
      logger.log('[Nominatim] Response data:', data);

      if (!data || data.length === 0) {
        logger.log('No results found for:', cleanQuery);
        return null;
      }

      const result = data[0];

      const geocodeResult = {
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
        displayName: result.display_name,
        city: result.address?.city || result.address?.town || result.address?.village || cleanQuery,
        country: result.address?.country || '',
      };

      logger.log('[Nominatim] Geocode result:', geocodeResult);

      return geocodeResult;
    } catch (error) {
      logger.error('Error geocoding with Nominatim:', error);
      return null;
    }
  },

  /**
   * Geocode using Mapbox - FREE tier: 100,000 requests/month
   * Best for: Production apps with moderate usage
   * Get API key: https://www.mapbox.com/
   */
  async geocodeWithMapbox(query: string, apiKey: string): Promise<GeocodeResult | null> {
    try {
      const cleanQuery = query.trim();
      if (!cleanQuery) return null;

      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(cleanQuery)}.json?access_token=${apiKey}&limit=1&types=place`;

      const response = await fetch(url);

      if (!response.ok) {
        logger.error('Mapbox geocoding failed:', response.status);
        return null;
      }

      const data = await response.json();

      if (!data.features || data.features.length === 0) {
        logger.log('No results found for:', cleanQuery);
        return null;
      }

      const result = data.features[0];
      const [lng, lat] = result.center;

      // Extract city and country from context
      const city = result.text || cleanQuery;
      let country = '';

      if (result.context) {
        const countryContext = result.context.find((c: any) => c.id.startsWith('country'));
        if (countryContext) country = countryContext.text;
      }

      return {
        lat,
        lng,
        displayName: result.place_name,
        city,
        country,
      };
    } catch (error) {
      logger.error('Error geocoding with Mapbox:', error);
      return null;
    }
  },

  /**
   * Geocode using Google Maps - Requires API key and billing
   * Best for: Enterprise apps
   * Get API key: https://developers.google.com/maps/documentation/geocoding/start
   */
  async geocodeWithGoogle(query: string, apiKey: string): Promise<GeocodeResult | null> {
    try {
      const cleanQuery = query.trim();
      if (!cleanQuery) return null;

      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(cleanQuery)}&key=${apiKey}`;

      const response = await fetch(url);

      if (!response.ok) {
        logger.error('Google geocoding failed:', response.status);
        return null;
      }

      const data = await response.json();

      if (data.status !== 'OK' || !data.results || data.results.length === 0) {
        logger.log('No results found for:', cleanQuery);
        return null;
      }

      const result = data.results[0];
      const location = result.geometry.location;

      // Extract city and country
      let city = cleanQuery;
      let country = '';

      if (result.address_components) {
        const cityComponent = result.address_components.find((c: any) =>
          c.types.includes('locality') || c.types.includes('administrative_area_level_2')
        );
        const countryComponent = result.address_components.find((c: any) =>
          c.types.includes('country')
        );

        if (cityComponent) city = cityComponent.long_name;
        if (countryComponent) country = countryComponent.long_name;
      }

      return {
        lat: location.lat,
        lng: location.lng,
        displayName: result.formatted_address,
        city,
        country,
      };
    } catch (error) {
      logger.error('Error geocoding with Google:', error);
      return null;
    }
  },

  /**
   * Main geocoding function - uses the best available method
   * Priority: Mapbox (if key provided) → Google (if key provided) → Nominatim (free)
   */
  async geocode(
    query: string,
    options?: {
      mapboxKey?: string;
      googleKey?: string;
    }
  ): Promise<GeocodeResult | null> {
    // Try Mapbox first if API key provided
    if (options?.mapboxKey) {
      const result = await this.geocodeWithMapbox(query, options.mapboxKey);
      if (result) return result;
    }

    // Try Google if API key provided
    if (options?.googleKey) {
      const result = await this.geocodeWithGoogle(query, options.googleKey);
      if (result) return result;
    }

    // Fall back to free Nominatim
    return this.geocodeWithNominatim(query);
  },

  /**
   * Reverse geocode - convert coordinates to address
   * Uses Nominatim (free)
   */
  async reverseGeocode(lat: number, lng: number): Promise<GeocodeResult | null> {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`;

      const response = await fetch(url, {
        headers: {
          'User-Agent': 'Dice-Cat-App/1.0',
        },
      });

      if (!response.ok) {
        logger.error('Reverse geocoding failed:', response.status);
        return null;
      }

      const result = await response.json();

      return {
        lat,
        lng,
        displayName: result.display_name,
        city: result.address?.city || result.address?.town || result.address?.village || '',
        country: result.address?.country || '',
      };
    } catch (error) {
      logger.error('Error reverse geocoding:', error);
      return null;
    }
  },
};